import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import { Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useCurrency, useI18n, useStaffCan } from '~Hooks';
import { truncateText } from '~Scenes/ParkedOrders/utils';
import { CompleteBongQuery_venue_company_items } from '~Scenes/POS/Queries/__queries__';
import { isServiceChargeItem } from '~Utils/serviceCharge';
import StockStatus from './StockStatus';
import ProductActions from './ProductActions';

const Row = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  background-color: var(--colorBlueGray1100);
  padding: 16px;
  min-height: 64px;
  border-bottom: 1px solid var(--colorBlueGray1200);

  &:first-of-type {
    border-radius: 8px 8px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 8px 8px;
    border-bottom: none;
  }

  &:only-of-type {
    border-radius: 8px;
  }
`;

const ImageBox = styled.img`
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 6px;
  object-fit: cover;
`;

const ImagePlaceholder = styled.div`
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 6px;
  background-color: var(--colorBlueGray1000);
`;

const NameColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 16px;
  min-width: 0;
`;

const AbsoluteCenter = styled(Box)<{ left: string }>`
  position: absolute;
  left: ${({ left }) => left};
  transform: translateX(-50%);
`;

const ItalicParagraph = styled(Paragraph)`
  font-style: italic;
`;

interface Props {
  item: CompleteBongQuery_venue_company_items;
  venueId: string;
  hasCostBasis: boolean;
}

const ProductRow: FC<Props> = ({ item, venueId, hasCostBasis }) => {
  const { i18n } = useI18n();
  const currency = useCurrency();
  const staffCan = useStaffCan();

  const imageUrl = item.image?.file?.url;
  const title = i18n(item.titleLang);
  const description = i18n(item.descriptionLang);

  if (isServiceChargeItem(title) || title.includes('[m]')) {
    return null;
  }

  return (
    <>
      <Row>
        <Box display="flex" alignItems="center" fluid>
          {imageUrl ? <ImageBox src={`${imageUrl}/64x64`} alt={title} /> : <ImagePlaceholder />}
          <NameColumn>
            <Heading level={5} color="var(--colorWhite)" bottom={0}>
              {title}
            </Heading>
            {description && (
              <Paragraph level={5} color="var(--colorBlueGray200)">
                {truncateText(description, 20)}
              </Paragraph>
            )}
          </NameColumn>
        </Box>

        {hasCostBasis && staffCan.REPORTS && (
          <AbsoluteCenter left="35%" display="flex" flexDirection="column" alignItems="flex-start">
            <Heading level={7} color="var(--colorBlueGray200)" bottom={0}>
              {i18n('Inventory.CostOfGoods')}
            </Heading>
            {item.costBasis != null && item.costBasis > 0 ? (
              <Paragraph fontWeight={600} level={4} color="var(--colorWhite)">
                {currency.format(item.costBasis)}
              </Paragraph>
            ) : (
              <ItalicParagraph fontWeight={600} level={4} color="var(--colorBlueGray200)">
                {i18n('Inventory.NotSet')}
              </ItalicParagraph>
            )}
          </AbsoluteCenter>
        )}

        <StockStatus itemId={item.id} />

        <ProductActions
          itemId={item.id}
          venueId={venueId}
          title={title}
          description={description}
          imageUrl={imageUrl}
        />
      </Row>
    </>
  );
};

export default memo(ProductRow);
